/* eslint-disable max-lines, max-lines-per-function */
import { useCallback, useEffect, useState } from "react";
import { BatchResourcesAclDc, Permissions } from "@evergis/api";
import { changeProps, removeObject, replaceObject, returnFound } from "find-and";

import { isConfigure, isRead, isWrite, togglePermissions } from "../../../../../utils/permissions";

import {
  CheckedEntities,
  PartialEntities,
  LayerStructureInfo,
  LayerStructureLayer,
  LayerStructureTable,
} from "../../types";

export const useStructure = (
  initialStructure?: LayerStructureInfo,
  activeAclRole?: string,
  pickedUsers?: string[],
  loading?: boolean,
) => {
  const [structure, setStructure] = useState<LayerStructureInfo | undefined>();
  const [updateData, setUpdateData] = useState<BatchResourcesAclDc>();
  const [checkedAll, setCheckedAll] = useState<CheckedEntities | undefined>(undefined);
  const [partial, setPartial] = useState<PartialEntities | undefined>(undefined);

  // Обновление настроек доступа одновременно для всех сущностей: таблицы, слои
  const bulkPermissionsUpdate = useCallback(
    (userRole: string, permissions: Permissions) => {
      if (!structure) return null;

      return {
        ...structure,
        tables: structure.tables.map(table => ({
          ...table,
          acl: {
            ...table.acl,
            data: !returnFound(table.acl.data, { role: userRole })
              ? [
                  ...table.acl.data,
                  {
                    role: userRole,
                    permissions,
                  },
                ]
              : isConfigure(table.permissions)
              ? changeProps(table.acl.data, { role: userRole }, { permissions })
              : table.acl.data,
          },
          layers: table.layers.map(layer => ({
            ...layer,
            acl: {
              ...layer.acl,
              data: !returnFound(layer.acl.data, { role: userRole })
                ? [
                    ...layer.acl.data,
                    {
                      role: userRole,
                      permissions,
                    },
                  ]
                : isConfigure(layer.permissions)
                ? changeProps(layer.acl.data, { role: userRole }, { permissions })
                : layer.acl.data,
            },
          })),
        })),
      };
    },
    [structure],
  );

  // Подсчет выбранных настроек доступа указанного типа (read/write/configure) в массиве сущностей
  const checkedEntities = useCallback(
    (
      entities: (LayerStructureTable | LayerStructureLayer)[],
      userRole: string,
      type: "read" | "write" | "configure",
    ): number => {
      if (!entities.length) {
        return 0;
      }

      let count = 0;

      entities.forEach(entity => {
        const { permissions } = entity.acl.data.find(item => item.role === userRole) || {};

        if (!permissions) {
          return false;
        }

        if (
          (type === "read" && isRead(permissions)) ||
          (type === "write" && isWrite(permissions)) ||
          (type === "configure" && isConfigure(permissions))
        ) {
          count += 1;
        }
      });

      return count;
    },
    [],
  );

  // Коллбэк, вызываемый при добавлении пользователя из выпадающего списка
  const onUserPermissionsInit = useCallback(
    (userRole: string) => {
      setStructure(bulkPermissionsUpdate(userRole, Permissions.Read));
    },
    [bulkPermissionsUpdate],
  );

  // Коллбэк, вызываемый при удалении пользователя (клик по крестику в соответствующем чипе)
  const onUserPermissionsRemove = useCallback(
    (userRole: string) => {
      setStructure({
        ...structure,
        tables: structure.tables.map(table => ({
          ...table,
          acl: {
            ...table.acl,
            data: removeObject(table.acl.data, { role: userRole }),
          },
          layers: table.layers.map(layer => ({
            ...layer,
            acl: {
              ...layer.acl,
              data: removeObject(layer.acl.data, { role: userRole }),
            },
          })),
        })),
      });
    },
    [structure],
  );

  // Обработка состояния "выделены все сущности в ряду" и "не выделено ни одной сущности в ряду" --
  // для соответствующего изменения настроек доступа глобально для проекта
  const processChecked = useCallback(
    (inputStructure: LayerStructureInfo) => {
      if (!inputStructure || !activeAclRole) {
        return;
      }

      const allLayers = inputStructure.tables.reduce((acc, curr) => [...acc, ...curr.layers], []);

      const tablesCount = inputStructure.tables.length;
      const layersCount = allLayers.length;
      const totalCount = tablesCount + layersCount;

      const roleName = activeAclRole;

      const checkedTablesRead = checkedEntities(inputStructure.tables, roleName, "read");
      const checkedTablesWrite = checkedEntities(inputStructure.tables, roleName, "write");
      const checkedTablesConfigure = checkedEntities(inputStructure.tables, roleName, "configure");

      const checkedLayersRead = checkedEntities(allLayers, roleName, "read");
      const checkedLayersWrite = checkedEntities(allLayers, roleName, "write");
      const checkedLayersConfigure = checkedEntities(allLayers, roleName, "configure");

      const totalRead = checkedTablesRead + checkedLayersRead;
      const totalWrite = checkedTablesWrite + checkedLayersWrite;
      const totalConfigure = checkedTablesConfigure + checkedLayersConfigure;

      setPartial({
        read: totalRead > 0 && totalRead < totalCount,
        write: totalWrite > 0 && totalWrite < totalCount,
        configure: totalConfigure > 0 && totalConfigure < totalCount,
      });

      setCheckedAll({
        read: totalRead === totalCount,
        write: totalWrite === totalCount,
        configure: totalConfigure === totalCount,
      });
    },
    [activeAclRole, checkedEntities],
  );

  // Коллбэк, вызываемый при клике на иконку типа доступа напротив заголовка "Настройки доступа"
  // (глобально для проекта)
  const onBulkUpdate = useCallback(
    (permissions: Permissions) => {
      const updatedStructure = bulkPermissionsUpdate(activeAclRole, permissions);

      setStructure(updatedStructure);
      processChecked(updatedStructure);
    },
    [activeAclRole, bulkPermissionsUpdate, processChecked],
  );

  // Коллбэк, вызываемый при клике на иконку типа доступа напротив таблицы или слоя
  const onUpdateEntity = useCallback(
    (entityName: string, entityType: string, toggle: "read" | "write" | "configure", permissions: Permissions) => {
      const currentObject = returnFound(structure, { name: entityName, type: entityType });
      const isEntityConfigurable = isConfigure(currentObject.permissions);

      if (!currentObject || Array.isArray(currentObject) || !isEntityConfigurable) {
        return;
      }

      const updatedStructure = replaceObject(structure, { name: entityName, type: entityType }, {
        ...currentObject,
        acl: {
          data: changeProps(
            currentObject.acl.data,
            { role: activeAclRole },
            {
              permissions: togglePermissions(permissions, toggle),
            },
          ),
        },
      } as LayerStructureInfo);

      setStructure(updatedStructure);
      processChecked(updatedStructure);
    },
    [structure, activeAclRole, processChecked],
  );

  // Обогащение acl.data при выборе пользователя:
  // Если данные для какой-либо сущности для пользователя отсутствуют -- добавляем None для него
  const enrichAclData = useCallback(
    (inputUser: string) => {
      if (!structure) {
        return;
      }

      setStructure({
        ...structure,
        tables: structure.tables.map(table => ({
          ...table,
          acl: {
            data: !returnFound(table.acl.data, { role: inputUser })
              ? [
                  ...table.acl.data,
                  {
                    role: inputUser,
                    permissions: Permissions.None,
                  },
                ]
              : table.acl.data,
          },
          layers: table.layers.map(layer => ({
            ...layer,
            acl: {
              data: !returnFound(layer.acl.data, { role: inputUser })
                ? [
                    ...layer.acl.data,
                    {
                      role: inputUser,
                      permissions: Permissions.None,
                    },
                  ]
                : layer.acl.data,
            },
          })),
        })),
      });
    },
    [structure],
  );

  // Подготовка данных для обновления acl сущностей и добавления сущностей в каталоги пользователей
  useEffect(() => {
    if (!structure || !pickedUsers) {
      return;
    }

    const allLayers = structure.tables.reduce((acc, curr) => [...acc, ...curr.layers], []);

    setUpdateData({
      tables: structure.tables
        .filter(table => isConfigure(table.permissions))
        .map(table => ({
          objectName: table.name,
          data: table.acl.data,
        })),
      layers: allLayers
        .filter(layer => isConfigure(layer.permissions))
        .map(layer => ({
          objectName: layer.name,
          data: layer.acl.data,
        })),
    });
  }, [structure, pickedUsers]);

  // Создание клона структуры проекта
  useEffect(() => {
    if (initialStructure !== undefined && structure === undefined && !loading) {
      setStructure(initialStructure);
      processChecked(initialStructure);
    }
  }, [initialStructure, processChecked, structure, loading]);

  // При переключении пользователя необходимо пересчитать
  // отмеченные права для вложенных сущностей
  useEffect(() => {
    processChecked(structure);
  }, [activeAclRole]); // eslint-disable-line

  return {
    structure,
    partial,
    updateData,
    onUserPermissionsInit,
    onUserPermissionsRemove,
    onBulkUpdate,
    onUpdateEntity,
    enrichAclData,
    checkedAll,
  } as const;
};
